import React, {useState, useEffect} from 'react';
import {Button, Input, Select, Switch, Form, message, Card, Typography, Modal, InputNumber, Result, Upload, Radio} from 'antd';
import type {UploadFile} from 'antd/es/upload/interface';
import {UploadOutlined} from '@ant-design/icons';
import campaignService from '../../../services/campaignService';
import countryService from '../../../services/countryService';
import cityService from '../../../services/cityService';
import stateService, {StateOption} from '../../../services/stateService';
import walletService from '../../../services/walletService';
import {useNavigate, useParams} from 'react-router-dom';
import {formatValue, useIsMobile} from '../../../utils/helpers';

import {loadStripe, Stripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';
import DepositForm from '../../Wallet/walletDetails/depositForm';
import RichText from '../../../components/molecules/Forms/RichText/component';
import DOMPurify from "dompurify";
import GeoRadiusPicker from '../../../components/organisms/Forms/GeoRadiusPicker/component';
import {
    buildGeographicLocation,
    resolveCampaignGeoFormValues,
    TargetingMode,
    validateClickRate
} from '../../../utils/campaignGeo';

const stripePK = process.env.REACT_APP_STRIPE_PK || '';
const stripePromise: Promise<Stripe | null> = loadStripe(stripePK);


const {Title} = Typography;
const {Option} = Select;
const US_COUNTRY_ISO2 = 'US';

const getFilenameFromSignedUrl = (signedUrl?: string) => {
    if (!signedUrl) return '';
    try {
        const url = new URL(signedUrl);

        const pathName = url.pathname.split('/').pop() || '';
        const pathFile = decodeURIComponent(pathName);
        if (pathFile) return pathFile;

        const cd = url.searchParams.get('response-content-disposition') || '';
        const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
        if (match?.[1]) return decodeURIComponent(match[1]);

        return 'file';
    } catch {
        return 'file';
    }
};

const toExistingUploadFile = (signedUrl: string, uid: string): UploadFile => ({
    uid,
    name: getFilenameFromSignedUrl(signedUrl),
    status: 'done',
    url: signedUrl,
});

const UpdateCampaign: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const [form] = Form.useForm();
    const isMobile = useIsMobile();
    const [showAssets, setShowAssets] = useState(false);
    const [isGlobal, setIsGlobal] = useState(true);
    const [targetingMode, setTargetingMode] = useState<TargetingMode>('location');
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [selectedState, setSelectedState] = useState<string | null>(null);
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [countries, setCountries] = useState<{ iso2: string; name: string }[]>([]);
    const [states, setStates] = useState<StateOption[]>([]);
    const [cities, setCities] = useState<string[]>([]);
    const [updatedCampaignData, setUpdatedCampaignData] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [walletBalance, setWalletBalance] = useState<number>(0);

    const [showDepositModal, setShowDepositModal] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [depositStep, setDepositStep] = useState<'input' | 'pay' | 'result'>('input');
    const [paymentOk, setPaymentOk] = useState(false);
    const [depositForm] = Form.useForm();

    const budget = Form.useWatch('initialBudget', form);
    const insufficientFunds = typeof budget === 'number' && budget > walletBalance;
    const isUnitedStatesSelected = selectedCountry === US_COUNTRY_ISO2;

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [mediaFile, setMediaFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [logoList, setLogoList] = useState<UploadFile[]>([]);
    const [thumbnailList, setThumbnailList] = useState<UploadFile[]>([]);
    const [mediaList, setMediaList] = useState<UploadFile[]>([]);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [videoList, setVideoList] = useState<UploadFile[]>([]);

    useEffect(() => {
        (async () => {
            try {
                const data = await walletService.getWallet();
                setWalletBalance(data.balance);
            } catch (err) {
                console.error('Failed to load wallet balance', err);
            }
        })();
    }, []);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await countryService.getCountries();
                setCountries(data);
            } catch (error) {
                console.error('Error fetching countries:', error);
                message.error('Failed to load countries.');
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const fetchCampaignData = async () => {
            try {
                const data = await campaignService.getCampaignById(id as string);
                const geoFormValues = resolveCampaignGeoFormValues(data.geographicLocation, countries);

                setUpdatedCampaignData(data);

                form.setFieldsValue({
                    title: data.title,
                    description: data.description,
                    targetURL: data.targetURL,
                    clickRate: data.clickRate,
                    initialBudget: data.initialBudget,
                    isGlobal: geoFormValues.isGlobal,
                    country: geoFormValues.countryValue,
                    state: geoFormValues.stateValue,
                    city: geoFormValues.cityValue,
                    geoRadius: geoFormValues.geoRadius,
                    reviewNeeded: data.reviewNeeded || false
                });

                setIsGlobal(geoFormValues.isGlobal);
                setTargetingMode(geoFormValues.targetingMode);
                setSelectedCountry(geoFormValues.countryValue);
                setSelectedState(geoFormValues.stateValue);
                setSelectedCity(geoFormValues.cityValue);

                if (geoFormValues.countryObj?.iso2 === US_COUNTRY_ISO2) {
                    const stateData = await stateService.getStatesByCountry(geoFormValues.countryObj.iso2);
                    setStates(stateData);
                }

                if (geoFormValues.countryObj && (geoFormValues.countryObj.iso2 !== US_COUNTRY_ISO2 || geoFormValues.stateValue)) {
                    const cityData = await cityService.getCitiesByCountry(
                        geoFormValues.countryObj.iso2,
                        geoFormValues.countryObj.iso2 === US_COUNTRY_ISO2 ? geoFormValues.stateValue : null
                    );
                    setCities(cityData);
                }

                const logoUrl = data.assets?.logo?.signedUrl;
                const thumbUrl = data.assets?.thumbnail?.signedUrl;
                const mediaUrl = data.assets?.media?.signedUrl;
                const videoUrl = data.assets?.video?.compressed?.signedUrl;

                setLogoList(logoUrl ? [toExistingUploadFile(logoUrl, 'existing-logo')] : []);
                setThumbnailList(thumbUrl ? [toExistingUploadFile(thumbUrl, 'existing-thumb')] : []);
                setMediaList(mediaUrl ? [toExistingUploadFile(mediaUrl, 'existing-media')] : []);
                setVideoList(videoUrl ? [toExistingUploadFile(videoUrl, 'existing-video')] : []);
            } catch (error) {
                console.error('Error fetching campaign:', error);
                message.error('Failed to load campaign details.');
            } finally {
                setLoading(false);
            }
        };

        fetchCampaignData();
    }, [id, form, countries]);

    const handleSubmit = async (values: any) => {
        if (submitting) return;

        const geographicLocation = buildGeographicLocation(values, isGlobal, targetingMode);

        const formData = new FormData();

        const safeToStoreDescription = DOMPurify.sanitize(values.description || '', {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li'],
            ALLOWED_ATTR: [],
        });

        formData.append('title', values.title);
        formData.append('description', safeToStoreDescription);
        formData.append('targetURL', values.targetURL);
        formData.append('clickRate', String(values.clickRate));
        formData.append('initialBudget', String(values.initialBudget));
        formData.append('geographicLocation', JSON.stringify(geographicLocation));
        formData.append('reviewNeeded', String(values.reviewNeeded));
        
        // Append files if they exist
        if (logoFile) formData.append('logo', logoFile);
        if (thumbnailFile) formData.append('thumbnail', thumbnailFile);
        if (mediaFile) formData.append('media', mediaFile);
        if (videoFile) formData.append('video', videoFile);

        setSubmitting(true);
        try {
            const result = await campaignService.updateCampaign(id as string, formData);
            if (result._id) {
                message.success('Campaign updated successfully!');
                setUpdatedCampaignData(result);
                setLogoFile(null);
                setThumbnailFile(null);
                setMediaFile(null);
                setVideoFile(null);
                navigate(`/campaigns/${result._id}`);
            } else {
                message.error('Failed to update campaign. Please try again.');
            }
        } catch (error: any) {
            console.error('Error updating campaign:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update campaign. Please try again.';
            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCountryChange = async (iso2: string) => {
        const countryObj = countries.find(country => country.iso2 === iso2);
        if (!countryObj) return;
        setSelectedCountry(countryObj.iso2);
        setCities([]);
        setStates([]);

        if (countryObj.iso2 === US_COUNTRY_ISO2) {
            const stateData = await stateService.getStatesByCountry(countryObj.iso2);
            setStates(stateData);
        }

        if (countryObj.iso2 !== US_COUNTRY_ISO2) {
            try {
                const cityData = await cityService.getCitiesByCountry(countryObj.iso2);
                setCities(cityData);
            } catch (error) {
                console.error('Error fetching cities:', error);
                message.error('Failed to load cities.');
            }
        }

        setSelectedCity(null);
        setSelectedState(null);
        form.setFieldsValue({state: null, city: null});
    };

    const handleStateChange = async (value?: string) => {
        const state = value || null;
        setSelectedState(state);
        setSelectedCity(null);
        setCities([]);
        form.setFieldsValue({state, city: null});

        if (!state || selectedCountry !== US_COUNTRY_ISO2) return;

        try {
            const cityData = await cityService.getCitiesByCountry(US_COUNTRY_ISO2, state);
            setCities(cityData);
        } catch (error) {
            console.error('Error fetching cities:', error);
            message.error('Failed to load cities.');
        }
    };

    const openDeposit = () => {
        setClientSecret(null);
        setDepositStep('input');
        setShowDepositModal(true);
    };

    const closeDeposit = () => {
        setShowDepositModal(false);
        depositForm.resetFields();
    };

    const refreshBalance = async () => {
        try {
            const data = await walletService.getWallet();
            setWalletBalance(data.balance);
            await form.validateFields(['initialBudget']).catch();
        } catch {
        }
    };

    const keepOnlyLast = (list: UploadFile[]) => (list?.length ? [list[list.length - 1]] : []);

    const beforeUpload = (file: File) => {
        const fileName = file.name.toLowerCase();

        const isVideo =
            file.type.startsWith('video/') ||
            fileName.endsWith('.mp4') ||
            fileName.endsWith('.mov') ||
            fileName.endsWith('.webm');

        const limit = isVideo
            ? 100 * 1024 * 1024 // 100MB
            : 5 * 1024 * 1024;  // 5MB images

        if (file.size > limit) {
            message.error(
                isVideo
                    ? 'Video must be smaller than 100 MB'
                    : 'Image must be smaller than 5 MB'
            );
            return Upload.LIST_IGNORE;
        }

        return false;
    };

    const onDepositAmountSubmit = async ({amount}: { amount: number }) => {
        if (!amount || amount <= 0) return;
        try {
            const res = await walletService.depositFunds({amount});
            setClientSecret(res.clientSecret);
            setDepositStep('pay');
        } catch (e: any) {
            message.error(e.message || 'Error creating payment intent');
            closeDeposit();
        }
    };


    if (loading) return <p>Loading...</p>;
    if (!updatedCampaignData) return <p>Campaign not found.</p>;

    return (
        <div style={{width: '100%', maxWidth: 1100, margin: '0 auto', padding: isMobile ? 12 : 20}}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: 650,
                    margin: '0 auto',
                    padding: isMobile ? 12 : 24,
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: 10,
                }}
                bodyStyle={{padding: isMobile ? 12 : 24}}
            >
                <Title level={4} className={"title"}>Update campaign</Title>
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{isGlobal: true}}>
                    <Form.Item label="Title" name="title" rules={[{required: true, message: 'Title is required'}]}>
                        <Input/>
                    </Form.Item>

                    <Form.Item label="Description" name="description">
                        <RichText placeholder="Write a description about your campaign..."/>
                    </Form.Item>

                    <Form.Item
                        label="Target URL"
                        name="targetURL"
                        rules={[
                            {required: true, message: 'Target URL is required'},
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    try {
                                        new URL(value);
                                        return Promise.resolve();
                                    } catch {
                                        return Promise.reject(new Error('Enter a valid URL (e.g., https://example.com)'));
                                    }
                                }
                            }
                        ]}
                    >
                        <Input/>
                    </Form.Item>

                    <Form.Item
                        label="Click rate ($ per click)"
                        name="clickRate"
                        dependencies={["country", "state", "city", "geoRadius"]}
                        rules={[
                            {required: true, message: "Click rate is required"},
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    const country = getFieldValue("country");
                                    const state = getFieldValue("state");
                                    const city = getFieldValue("city");
                                    const geoRadius = getFieldValue("geoRadius");

                                    return validateClickRate(value, country, state, city, geoRadius);
                                },
                            }),
                        ]}
                    >
                        <Input type="number" step="0.01"/>
                    </Form.Item>

                    <Form.Item
                        label="Initial budget ($)"
                        name="initialBudget"
                        validateStatus={insufficientFunds ? 'error' : ''}
                        rules={[
                            {required: true, message: 'Initial budget is required'},
                            {
                                validator: (_, value) => {
                                    if (value == null) return Promise.resolve();
                                    if (value < 10) return Promise.reject(new Error('Budget must be at least $10'));
                                    return Promise.resolve();
                                },
                            },
                        ]}
                        getValueFromEvent={(e) => {
                            const raw = e?.target?.value;
                            if (raw === '' || raw == null) return undefined;
                            const n = Number(raw);
                            return Number.isNaN(n) ? undefined : n;
                        }}
                        help={
                            insufficientFunds ? (
                                <span
                                    className="ant-form-item-explain-error">Insufficient funds: you have ${formatValue(walletBalance)}.{' '}
                                    <Button type="link" size="small" onClick={() => openDeposit()}>Deposit</Button>
                                </span>
                            ) : null
                        }
                    >
                        <Input type="number" step="1"/>
                    </Form.Item>

                    <Form.Item
                        label="Review needed"
                        name="reviewNeeded"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                        />
                    </Form.Item>

                    <Form.Item label="Geographic targeting">
                        <Switch checked={isGlobal} onChange={(checked) => {
                            setIsGlobal(checked);
                            if (checked) {
                                setSelectedCountry(null);
                                setSelectedState(null);
                                setSelectedCity(null);
                                setTargetingMode('location');
                                form.setFieldsValue({country: null, state: null, city: null, geoRadius: null});
                            }
                        }} checkedChildren="Global" unCheckedChildren="Custom"/>
                    </Form.Item>

                    {!isGlobal && (
                        <>
                            <Form.Item label="Targeting method">
                                <Radio.Group
                                    value={targetingMode}
                                    onChange={(event) => {
                                        const nextMode = event.target.value;
                                        setTargetingMode(nextMode);
                                        if (nextMode === 'map') {
                                            setSelectedCountry(null);
                                            setSelectedState(null);
                                            setSelectedCity(null);
                                            setStates([]);
                                            setCities([]);
                                            form.setFieldsValue({country: null, state: null, city: null, geoRadius: null});
                                        } else {
                                            form.setFieldsValue({geoRadius: null});
                                        }
                                    }}
                                >
                                    <Radio.Button value="location">Country / state / city</Radio.Button>
                                    <Radio.Button value="map">Pick on map</Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            {targetingMode === 'location' ? (
                                <>
                                    <Form.Item label="Country" name="country"
                                               rules={[{required: true, message: "Select a country"}]}>
                                        <Select showSearch value={selectedCountry} onChange={handleCountryChange}
                                                placeholder="Country">
                                            {countries.map((country) => (
                                                <Option key={country.iso2} value={country.iso2}>{country.name}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    {isUnitedStatesSelected && (
                                        <Form.Item label="State" name="state"
                                                   rules={[{required: true, message: "Select a state"}]}>
                                            <Select showSearch value={selectedState} onChange={handleStateChange} placeholder="State">
                                                {states.map((state) => (
                                                    <Option key={state.code} value={state.name}>{state.name}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                    <Form.Item label="City" name="city">
                                        <Select showSearch allowClear value={selectedCity} onChange={(value) => {
                                            setSelectedCity(value || null);
                                            form.setFieldsValue({city: value || null});
                                        }} placeholder="City (Optional)" disabled={!selectedCountry || (isUnitedStatesSelected && !selectedState)}>
                                            {cities.map((city) => (
                                                <Option key={city} value={city}>{city}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </>
                            ) : (
                                <Form.Item
                                    label="Target area"
                                    name="geoRadius"
                                    rules={[{required: true, message: "Choose a location and radius"}]}
                                >
                                    <GeoRadiusPicker/>
                                </Form.Item>
                            )}
                        </>
                    )}

                    <Form.Item label="Campaign assets">
                        <Switch
                            checked={showAssets}
                            onChange={(checked) => {
                                setShowAssets(checked);
                                if (!checked) {
                                    setLogoFile(null);
                                    setThumbnailFile(null);
                                    setMediaFile(null);
                                    setVideoFile(null);
                                }
                            }}
                            checkedChildren="Show"
                            unCheckedChildren="Hide"
                        />
                    </Form.Item>

                    {showAssets && (
                        <div style={{
                            padding: isMobile ? 10 : 12,
                            border: '1px solid #f0f0f0',
                            borderRadius: 8,
                            marginBottom: 12,
                            background: '#fafafa',
                        }}>
                            <Form.Item label="Logo" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    fileList={logoList}
                                    listType="text"
                                    showUploadList={{showPreviewIcon: false}}
                                    onRemove={() => {
                                        setLogoFile(null);
                                        setLogoList([]);
                                    }}
                                    onChange={(info) => {
                                        const next = keepOnlyLast(info.fileList);
                                        setLogoList(next);

                                        const f = next?.[0]?.originFileObj;
                                        setLogoFile(f instanceof File ? f : null);
                                    }}

                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>Upload logo</Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Thumbnail" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    fileList={thumbnailList}
                                    listType="text"
                                    showUploadList={{showPreviewIcon: false}}
                                    onRemove={() => {
                                        setThumbnailFile(null);
                                        setThumbnailList([]);
                                    }}
                                    onChange={(info) => {
                                        const next = keepOnlyLast(info.fileList);
                                        setThumbnailList(next);

                                        const f = next?.[0]?.originFileObj;
                                        setThumbnailFile(f instanceof File ? f : null);
                                    }}

                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>Upload thumbnail</Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Media" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    fileList={mediaList}
                                    listType="text"
                                    showUploadList={{showPreviewIcon: false}}
                                    onRemove={() => {
                                        setMediaFile(null);
                                        setMediaList([]);
                                    }}
                                    onChange={(info) => {
                                        const next = keepOnlyLast(info.fileList);
                                        setMediaList(next);

                                        const f = next?.[0]?.originFileObj;
                                        setMediaFile(f instanceof File ? f : null);
                                    }}

                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>Upload media</Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Video" extra="MP4 • Max 100 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="video/mp4"
                                    fileList={videoList}
                                    listType="text"
                                    showUploadList={{ showPreviewIcon: false }}
                                    onRemove={() => {
                                        setVideoFile(null);
                                        setVideoList([]);
                                    }}
                                    onChange={(info) => {
                                        const next = keepOnlyLast(info.fileList);
                                        setVideoList(next);

                                        const f = next?.[0]?.originFileObj;
                                        setVideoFile(f instanceof File ? f : null);
                                    }}
                                >
                                    <Button block={isMobile} icon={<UploadOutlined />}>
                                        Upload video
                                    </Button>
                                </Upload>
                            </Form.Item>
                        </div>
                    )}

                    <Form.Item style={{marginBottom: 0}} shouldUpdate>
                        {() => {
                            const hasErrors = form.getFieldsError().some(({errors}) => errors.length);
                            const budgetVal = form.getFieldValue('initialBudget');
                            const insufficientFunds = typeof budgetVal === 'number' && budgetVal > walletBalance;

                            return (
                                <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block={isMobile}
                                        disabled={hasErrors || insufficientFunds || submitting}
                                        loading={submitting}
                                        style={{width: isMobile ? '100%' : 'auto'}}
                                    >
                                        {submitting ? 'Updating...' : 'Update campaign'}
                                    </Button>
                                </div>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Card>

            <Modal
                open={showDepositModal}
                title="Deposit Funds"
                onCancel={closeDeposit}
                footer={null}
                destroyOnClose
                forceRender
                width={isMobile ? '92vw' : 520}
                styles={{body: {padding: isMobile ? 12 : 24}}}
                afterOpenChange={(open) => {
                    if (open && depositStep === 'input') {
                        const initial = Number(form.getFieldValue('initialBudget') ?? 0);
                        const neededRaw = initial - (walletBalance ?? 0);
                        const prefill = Math.max(20, Math.round(Math.max(0, neededRaw) * 100) / 100);
                        depositForm.setFieldsValue({amount: prefill});
                    }
                }}
            >
                {depositStep === 'input' && (
                    <Form form={depositForm} layout="vertical" onFinish={onDepositAmountSubmit}>
                        <Form.Item
                            label="Amount (USD)"
                            name="amount"
                            rules={[
                                {required: true, message: 'Please enter an amount'},
                                {validator: (_, v) => (v >= 20 ? Promise.resolve() : Promise.reject(new Error('Minimum deposit is $20')))},
                            ]}
                        >
                            <InputNumber style={{width: '100%'}} min={0}/>
                        </Form.Item>
                        <div style={{textAlign: 'right'}}>
                            <Button onClick={closeDeposit} style={{marginRight: 8}}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Continue
                            </Button>
                        </div>
                    </Form>
                )}

                {depositStep === 'pay' && clientSecret && (
                    <Elements stripe={stripePromise} options={{clientSecret}}>
                        <DepositForm
                            onSuccess={() => {
                                setPaymentOk(true);
                                setDepositStep('result');
                            }}
                            onError={() => {
                                setPaymentOk(false);
                                setDepositStep('result');
                            }}
                        />
                    </Elements>
                )}

                {depositStep === 'result' &&
                    (paymentOk ? (
                        <Result
                            status="success"
                            title="Deposit successful!"
                            extra={[
                                <Button
                                    key="back"
                                    type="primary"
                                    onClick={async () => {
                                        closeDeposit();
                                        await refreshBalance();
                                        message.success('Balance updated');
                                    }}
                                >
                                    Back
                                </Button>,
                            ]}
                        />
                    ) : (
                        <Result
                            status="error"
                            title="Payment failed"
                            subTitle="Please try again."
                            extra={[
                                <Button key="retry" onClick={() => setDepositStep('input')}>
                                    Try Again
                                </Button>,
                            ]}
                        />
                    ))}
            </Modal>
        </div>
    );
};

export default UpdateCampaign;